import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faTriangleExclamation,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import SEO, { pageSEO } from "../components/seo.component";
import constants from "../constants/constants";
import ListingCard from "../components/listingCard.component";
import Pagination from "../components/pagination.component";
import BouncingDotsLoader from "./loader.component";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../functions/animations";
import { formatStr } from "../functions/listings";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";
import imgs from "./temp.json";

export default function Listings() {
  const { state } = useLocation();
  const seoConfig = pageSEO.listings();
  const maxListings = 15;
  const [listings, setListings] = useState([]);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("All");
  const [error, setError] = useState("");
  const listingsRef = useRef(null);

  //dummy listings
  const demoListings = data.value.filter(
    listing =>
      (listing.PropertyType || "").toLowerCase().includes("residential")
  );

  const [searchParams, setSearchParams] = useState({
    bedrooms: "",
    bathrooms: "",
    minPrice: "",
    maxPrice: "",
    cityArea: "",
    address: ""
  });

  const handlePageChange = (newPage) => {
    setPage(newPage);

    if (listingsRef.current) {
      listingsRef.current.scrollIntoView({
        block: "start",
      });
    }
  };

  /* API REQUEST*/
  async function fetchData(url) {
    const body = {
      url,
      needAuth: true,
      bearerToken: constants.TOKEN
    };
    const response =
      constants.ENVIRONMENT === "production"
        ? await fetch(
          `https://${constants.DOMAIN}/api/${url}`
        )
        :
        await fetch(
          `http://${constants.URL}/metaGet`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
          }
        );
    if (!response.ok)
      throw Error("API ERROR");
    return response.json();
  }

  /* FORMAT IMAGES */
  function formatImages(media) {
    return media.reduce((acc, item) => {
      if (!acc[item.ResourceRecordKey])
        acc[item.ResourceRecordKey] = [];
      acc[item.ResourceRecordKey].push(item);
      return acc;
    }, {});
  }

  /* LOAD MEDIA */
  async function loadImages(data) {
    if (!data.length) return;

    const ids = data
      .map(item => `'${item.ListingKey}'`)
      .join(",");

    let url;

    if (constants.ENVIRONMENT === "production") {
      url =
        `odata/Media(Q)$filter=ResourceRecordKey(S)in(S)(${ids})(S)and(S)ImageSizeDescription(S)eq(S)'Thumbnail'(S)and(S)PreferredPhotoYN(S)eq(S)true`;
    } else {
      url =
        `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey in (${ids}) and ImageSizeDescription eq 'Thumbnail' and PreferredPhotoYN eq true`;
    }

    const result = await fetchData(url);

    setImages(formatImages(result.value));
  }

  /* LOAD LISTINGS */
  async function loadListings() {
    try {
      setLoading(true);

      let filterQuery =
        "StandardStatus eq 'Active'";

      if (filter === "Agent") {
        filterQuery +=
          ` and ListAgentKey eq '${constants.AGENT_ID}'`;
      }

      if (filter === "Office") {
        filterQuery +=
          ` and ListOfficeKey eq '${constants.OFFICE_ID}'`;
      }

      Object.values(searchParams)
        .filter(Boolean)
        .forEach(item => {
          filterQuery +=
            ` and ${item}`;
        });

      // Format only for production
      const finalFilter =
        constants.ENVIRONMENT === "production"
          ? formatStr(filterQuery)
          : filterQuery;

      const url = constants.ENVIRONMENT === "production" ?
        `odata/Property(Q)$filter=${finalFilter}(A)$orderby=OriginalEntryTimestamp(S)desc(A)$top=500`
        :
        `https://query.ampre.ca/odata/Property?$filter=${finalFilter}&$orderby=OriginalEntryTimestamp desc&$top=500`;

      const data =
        await fetchData(url);

      console.log("data", data);

      if (data.value) {
        setListings(data.value);

        await loadImages(
          data.value.slice(
            0,
            maxListings
          )
        );
        setError("");
      }
    }
    catch (error) {
      console.log(error);
      setListings([]);
      setImages({});

      // setError(
      //   "Unable to load listings."
      // );
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    console.log("FILTER", filter)
    loadListings()
  }, [filter, searchParams]);

  useEffect(() => {
    async function updatePageImages() {
      const data =
        listings.slice(
          (page - 1) * maxListings,
          page * maxListings
        );

      await loadImages(data);
    }
    if (listings.length) {
      updatePageImages();
    }
  }, [page]);

  /* SEARCH UPDATE */
  function updateSearch(key, value) {
    setSearchParams(prev => ({
      ...prev,
      [key]: value
    }));
  }

  function handleSearch() {
    setPage(1);
  }

  function clearSearch() {
    setSearchParams({
      bedrooms: "",
      bathrooms: "",
      minPrice: "",
      maxPrice: "",
      cityArea: "",
      address: ""
    });

    setFilter("All");
    setPage(1);
  }

  /* PAGINATION */
  // const totalPages =
  //   Math.ceil(
  //     listings.length / maxListings
  //   );

  // const visibleListings =
  //   listings.slice(
  //     (page - 1) * maxListings,
  //     page * maxListings
  //   );

  const totalPages =
    Math.ceil(
      demoListings.length / maxListings
    );

  const visibleListings =
    demoListings.slice(
      (page - 1) * maxListings,
      page * maxListings
    );

  console.log("LSITINGS", listings)
  console.log("VISIBLE", visibleListings)

  return (
    <>
      <SEO {...seoConfig} />

      <section className="max-w-[1600px] mx-auto px-6 pt-36 pb-20">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          className="mx-auto mt-12 max-w-6xl rounded-3xl bg-white p-8 shadow-2xl lg:p-12">
          {/* Header */}
          <div className="mb-10 text-center">
            <motion.p
              variants={fadeUp}
              className="text-sm uppercase tracking-[0.4em] text-summit-highlight">
              Property Search
            </motion.p>

            <motion.h2
              variants={fadeUp}
              className="mt-4 text-4xl font-light uppercase tracking-wider text-summit-primary">
              Find Your Dream Home
            </motion.h2>

            <motion.div
              variants={fadeUp}
              className="mx-auto mt-5 h-0.5 w-24 bg-summit-highlight" />
          </div>

          {/* Search Fields */}
          <motion.div
            variants={fadeUp}
            className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm uppercase tracking-wider text-slate-600">
                Bedrooms
              </label>

              <select
                value={searchParams.bedrooms}
                onChange={(e) =>
                  updateSearch(
                    "bedrooms",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-summit-highlight focus:bg-white"
              >
                <option value="">Select</option>
                <option value="BedroomsTotal eq 1">1</option>
                <option value="BedroomsTotal eq 2">2</option>
                <option value="BedroomsTotal eq 3">3</option>
                <option value="BedroomsTotal ge 3">3+</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm uppercase tracking-wider text-slate-600">
                Bathrooms
              </label>

              <select
                value={searchParams.bathrooms}
                onChange={(e) =>
                  updateSearch(
                    "bathrooms",
                    e.target.value
                  )
                }
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-summit-highlight focus:bg-white"
              >
                <option value="">Select</option>
                <option value="BathroomsTotalInteger eq 1">1</option>
                <option value="BathroomsTotalInteger eq 2">2</option>
                <option value="BathroomsTotalInteger eq 3">3</option>
                <option value="BathroomsTotalInteger ge 3">3+</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm uppercase tracking-wider text-slate-600">
                City / Community
              </label>

              <input
                value={
                  searchParams.cityArea
                    ? searchParams.cityArea.match(/'([^']+)'/)?.[1] || ""
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value.trim();
                  if (value) {
                    const city =
                      value.charAt(0).toUpperCase() +
                      value.slice(1);

                    updateSearch(
                      "cityArea",
                      `(contains(City, '${city}') or contains(CityRegion, '${city}') or contains(UnparsedAddress, '${city}'))`
                    );
                  } else {
                    updateSearch("cityArea", "");
                  }
                }}
                placeholder="Toronto"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-summit-highlight focus:bg-white"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm uppercase tracking-wider text-slate-600">
                Address
              </label>

              <input
                value={
                  searchParams.address
                    ? searchParams.address.match(/'([^']+)'/)?.[1] || ""
                    : ""
                }
                onChange={(e) => {
                  const value = e.target.value.trim();
                  if (value) {
                    const capitalizedValue =
                      value.charAt(0).toUpperCase() +
                      value.slice(1);

                    updateSearch(
                      "address",
                      `contains(UnparsedAddress, '${capitalizedValue}') or contains(StreetName, '${capitalizedValue}') or contains(StreetNumber, '${capitalizedValue}')`
                    );
                  } else {
                    updateSearch("address", "");
                  }
                }}
                placeholder="Street Address"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-summit-highlight focus:bg-white"
              />
            </div>


            <div>
              <label className="mb-2 block text-sm uppercase tracking-wider text-slate-600">
                Minimum Price
              </label>

              <input
                value={
                  searchParams.minPrice.replace(/\D/g, "")
                }
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  updateSearch(
                    "minPrice",
                    value
                      ? `ListPrice ge ${value}`
                      : ""
                  );
                }}
                placeholder="$250,000"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-summit-highlight focus:bg-white"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm uppercase tracking-wider text-slate-600">
                Maximum Price
              </label>

              <input
                value={
                  searchParams.maxPrice.replace(/\D/g, "")
                }
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  updateSearch(
                    "maxPrice",
                    value
                      ? `ListPrice le ${value}`
                      : ""
                  );
                }}
                placeholder="$900,000"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-5 py-4 outline-none transition focus:border-summit-highlight focus:bg-white"
              />
            </div>
          </motion.div>

          {/* Button */}
          <motion.div
            variants={fadeUp}
            className="w-full mt-10 flex flex-col md:flex-row gap-3 justify-end">
            <button
              onClick={handleSearch}
              className="rounded-xl bg-summit-primary px-12 py-4 text-sm uppercase tracking-[0.3em] text-white transition-all duration-300 hover:bg-summit-highlight"
            >
              Search Properties
            </button>

            <button
              onClick={clearSearch}
              className="btn-luxury-outline rounded-xl"
            >
              Clear
            </button>
          </motion.div>

        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          ref={listingsRef}
        >
          <motion.div
            variants={fadeUp}
            className="mt-24 flex flex-col gap-6 border-b border-slate-200 pb-8 sm:flex-row sm:items-end sm:justify-between mx-5 lg:mx-24">
            <div>
              <p className="mb-3 text-sm uppercase tracking-[0.4em] text-summit-highlight">
                Property Collection
              </p>

              <h1 className="text-4xl font-light uppercase tracking-wider text-summit-primary libre">
                Recent Listings
              </h1>
            </div>

            <div className="w-full sm:w-52">
              <label className="mb-2 block text-xs uppercase tracking-[0.25em] text-slate-500">
                Show Listings
              </label>

              <div className="relative">
                <select
                  className="w-full appearance-none rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm uppercase tracking-wider text-slate-700 shadow-sm outline-none transition focus:border-summit-highlight"
                  value={filter}
                  onChange={(e) => {
                    setPage(1);
                    setFilter(e.target.value);
                    // setSearching(4);
                    // runSearch(e.target.value);
                  }}
                >
                  <option value="All">
                    All Listings
                  </option>

                  <option value="Office">
                    Office Listings
                  </option>

                  <option value="Agent">
                    Agent Listings
                  </option>
                </select>

                <div className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-summit-highlight">
                  ▼
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
        {loading && (
          <div className="text-center py-20">
            <div className="mt-32 min-h-[20vh]">
              <BouncingDotsLoader />
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-20">

            <div className="inline-block rounded-xl border border-red-200 bg-red-50 px-6 py-4 text-red-700">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mr-2"
              />
              {error}
            </div>
          </div>
        )}

        {!loading && !error && visibleListings.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 py-10 px-4 lg:px-24">
            {visibleListings.map((property) => (
              <ListingCard
                key={property.ListingKey}
                property={property}
                // image={images[property.ListingKey]?.[0]}
                image={property.MainImgUrl}
                type="residential"
              />
            ))}
          </div>
        )}

        {!loading && !error && visibleListings.length === 0 && (
          <div className="text-center py-20 tracking-wider text-xl">
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="mr-2 text-summit-highlight"
            />
            No properties to show
          </div>
        )}

        <Pagination
          page={page}
          setPage={handlePageChange}
          totalPages={totalPages}
          totalItems={listings.length}
          pageSize={maxListings}
        />
      </section>
    </>
  );
}